import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { fetchChallengesWithStatus } from 'services/challenge.service';
import { buscarIniciativasFavoritas } from 'services/iniciativa.service';
import { HeaderElbb } from '../HomeElbb/components/Header';
import { ProgressoGeral } from '../MinhaJornada/components/ProgressoGeral';
import { TarefaCard } from '../MinhaJornada/components/TarefaCard';
import { BadgeItem } from '../MinhaJornada/components/BadgeItem';
import { IniciativaCardElbb } from '../HomeElbb/components/IniciativaCardElbb';
import { filterChallengesByJourneyProfile } from '../HomeElbb/utils/journeyChallenges';
import './style.scss';

// Mock frontend-only enquanto a integração de extrato ELBB com o backend não
// é definida. As três variantes principais (classificada etapa 1, classificada
// etapa 2 e 1º lugar) cobrem a representação do protótipo.
/* const EXTRATO_MOCK_ELBB: ExtratoIniciativaElbb[] = [
  {
    id: 'extrato-elbb-classificada-etapa1',
    titulo: 'Consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore.',
    variant: 'classificada-etapa1',
    categoriaLabel: 'Prêmio ELBB',
    participacaoConfirmada: true,
    valores: [
      { rotulo: 'Valor Bruto', valor: 'R$5555.56' },
      { rotulo: 'Valor Bruto', valor: 'R$1527.78' },
      { rotulo: 'Valor Bruto', valor: 'R$4027.78' },
    ],
  },
  {
    id: 'extrato-elbb-classificada-etapa2',
    titulo: 'Consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore.',
    variant: 'classificada-etapa2',
    categoriaLabel: 'Prêmio ELBB',
    participacaoConfirmada: true,
    valores: [
      { rotulo: 'Valor Bruto', valor: 'R$5555.56' },
      { rotulo: 'Valor Bruto', valor: 'R$1527.78' },
      { rotulo: 'Valor Bruto', valor: 'R$4027.78' },
    ],
  },
  {
    id: 'extrato-elbb-podio-1',
    titulo: 'Consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore.',
    variant: 'podio-1',
    categoriaLabel: 'Prêmio ELBB',
    participacaoConfirmada: true,
    valores: [
      { rotulo: 'Valor Bruto', valor: 'R$5555.56' },
      { rotulo: 'Valor Bruto', valor: 'R$1527.78' },
      { rotulo: 'Valor Bruto', valor: 'R$4027.78' },
    ],
  },
]; */

export function MinhaJornadaElbb() {
  const { user } = useAuth();
  const location = useLocation();
  const [tarefas, setTarefas] = useState<any[]>([]);
  const [badges, setBadges] = useState<any[]>([]);
  const [iniciativasFavoritas, setIniciativasFavoritas] = useState<any[]>([]);
  const [loadingChallenges, setLoadingChallenges] = useState(true);
  const [loadingFavoritas, setLoadingFavoritas] = useState(true);

  useEffect(() => {
    async function loadChallenges() {
      if (!user?._id) return;
      
      try {
        setLoadingChallenges(true);
        const challengesData = await fetchChallengesWithStatus(user._id);
        const filteredChallenges = filterChallengesByJourneyProfile(challengesData, user);
        
        const sortedChallenges = [...filteredChallenges].sort((a, b) => {
          if (a.concluida && !b.concluida) return -1;
          if (!a.concluida && b.concluida) return 1;
          return 0;
        });
        
        setTarefas(sortedChallenges);
        
        const sortedBadges = sortedChallenges.map(challenge => ({
          id: challenge.id,
          nome: challenge.titulo,
          imagem: challenge.icone,
          conquistado: challenge.concluida
        }));
        
        setBadges(sortedBadges);
      } catch (error) {
        console.error('Erro ao carregar desafios:', error);
      } finally {
        setLoadingChallenges(false);
      }
    }
    
    loadChallenges();
  }, [user?._id, user?.extra, user?.extra?.habilitado, user?.extra?.is_manager]);

  useEffect(() => {
    async function loadFavoritas() {
      if (!user?._id) return;
      
      try {
        setLoadingFavoritas(true);
        const favoritas = await buscarIniciativasFavoritas(user._id, 'ELBB');
        setIniciativasFavoritas(favoritas);
      } catch (error) {
        setIniciativasFavoritas([]);
      } finally {
        setLoadingFavoritas(false);
      }
    }
    
    loadFavoritas();
  }, [user?._id]);

  // Scroll para badges quando carregamento concluir e hash estiver presente
  useEffect(() => {
    if (!loadingChallenges && location.hash === '#badges') {
      setTimeout(() => {
        const badgesSection = document.querySelector('.colecao-badges');
        if (badgesSection) {
          const headerOffset = 80;
          const elementPosition = badgesSection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 300);
    } else if (!loadingChallenges && !location.hash) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [loadingChallenges, location.hash]);

  if (!user) return null;

  const tarefasConcluidas = tarefas.filter(t => t.concluida).length;
  const totalTarefas = tarefas.length;
  const percentualProgresso = totalTarefas > 0 ? Math.round((tarefasConcluidas / totalTarefas) * 100) : 0;

  return (
    <div id="minha-jornada-elbb-wrapper">
      <HeaderElbb 
        currentPage="minha-jornada"
      />

      <section className="hero-jornada-elbb" role="banner" aria-label="Minha Jornada no Prêmio ELBB">
        <div className="hero-jornada-elbb__content">
          <div className="hero-jornada-elbb__text">
            <h1 className="hero-jornada-elbb__title" tabIndex={0}>
              Minha Jornada no <span className="hero-jornada-elbb__highlight">Prêmio ELBB</span>
            </h1>
            <p className="hero-jornada-elbb__subtitle" tabIndex={0}>
              Complete desafios e desbloqueie<br />
              badges exclusivos
            </p>
          </div>
        </div>
      </section>

      <main className="jornada-elbb-main">
        <div className="jornada-elbb-main__container">
          <ProgressoGeral
            tarefasConcluidas={tarefasConcluidas}
            totalTarefas={totalTarefas}
            percentual={percentualProgresso}
          />

          {/* <ExtratoElbb iniciativas={EXTRATO_MOCK_ELBB} totalLabel="R$000.00" /> */}

          <section className="lista-tarefas" aria-labelledby="lista-tarefas-title-elbb">
            <h2 className="lista-tarefas__title" id="lista-tarefas-title-elbb" tabIndex={0}>Lista de tarefas</h2>
            {loadingChallenges ? (
              <div className="flex justify-center items-center py-8" role="status" aria-live="polite">
                <span className="text-primary-elbb">Carregando tarefas...</span>
              </div>
            ) : (
              <div className="lista-tarefas__wrapper">
                <div className="lista-tarefas__grid" role="list" aria-label="Lista de tarefas">
                  {tarefas.map((tarefa) => (
                    <TarefaCard 
                      key={tarefa.id}
                      titulo={tarefa.titulo}
                      descricao={tarefa.descricao}
                      concluida={tarefa.concluida}
                      icone={tarefa.icone}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="colecao-badges" aria-labelledby="colecao-badges-title-elbb">
            <h2 className="colecao-badges__title" id="colecao-badges-title-elbb" tabIndex={0}>Coleção de badges</h2>
            {loadingChallenges ? (
              <div className="flex justify-center items-center py-8" role="status" aria-live="polite">
                <span className="text-primary-elbb">Carregando badges...</span>
              </div>
            ) : (
              <div className="colecao-badges__wrapper">
                <div className="colecao-badges__grid" role="list" aria-label="Coleção de badges">
                  {badges.map((badge) => (
                    <BadgeItem 
                      key={badge.id}
                      nome={badge.nome}
                      imagem={badge.imagem}
                      conquistado={badge.conquistado}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="iniciativas-favoritas" aria-labelledby="favoritas-title-elbb">
            <h2 className="iniciativas-favoritas__title" id="favoritas-title-elbb" tabIndex={0}>Suas iniciativas favoritas</h2>
            {loadingFavoritas ? (
              <div className="flex justify-center items-center py-8" role="status" aria-live="polite">
                <span className="text-primary-elbb">Carregando iniciativas favoritas...</span>
              </div>
            ) : iniciativasFavoritas.length > 0 ? (
              <>
                <div className="iniciativas-favoritas__grid" role="list" aria-label="Iniciativas favoritas">
                  {iniciativasFavoritas.slice(0, 3).map((iniciativa) => (
                    <IniciativaCardElbb
                      key={iniciativa.id}
                      {...iniciativa}
                    />
                  ))}
                </div>
                {iniciativasFavoritas.length > 3 && (
                  <button className="iniciativas-favoritas__ver-mais" aria-label="Ver mais iniciativas favoritas">VER MAIS</button>
                )}
              </>
            ) : (
              <div className="iniciativas-favoritas__empty" role="status">
                <p tabIndex={0}>Você ainda não favoritou nenhuma iniciativa.</p>
                <p tabIndex={0}>Explore as iniciativas e salve suas favoritas!</p>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
